import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, CreditCard, Link2, Palette, Wallet, Banknote,
  ArrowLeftRight, KeyRound, Webhook, Code2, Settings as SettingsIcon,
  LifeBuoy, LogOut, Menu, X,
} from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", end: true, icon: LayoutDashboard, label: "Genel Bakış" },
  { to: "/dashboard/payments", icon: CreditCard, label: "Ödemeler" },
  { to: "/dashboard/payment-links", icon: Link2, label: "Ödeme Linkleri" },
  { to: "/dashboard/checkout", icon: Palette, label: "Checkout" },
  { to: "/dashboard/wallet", icon: Wallet, label: "Cüzdan" },
  { to: "/dashboard/payout", icon: Banknote, label: "Para Çekme" },
  { to: "/dashboard/transactions", icon: ArrowLeftRight, label: "İşlemler" },
  { to: "/dashboard/api", icon: KeyRound, label: "API" },
  { to: "/dashboard/webhooks", icon: Webhook, label: "Webhooklar" },
  { to: "/dashboard/developers", icon: Code2, label: "Geliştiriciler" },
  { to: "/dashboard/settings", icon: SettingsIcon, label: "Ayarlar" },
  { to: "/dashboard/support", icon: LifeBuoy, label: "Destek" },
];

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = async () => { await logout(); nav("/login"); };

  const SidebarContent = () => (
    <>
      <div className="px-5 py-5"><Logo /></div>
      <nav className="flex-1 space-y-1 px-3 overflow-y-auto">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.to.split("/").pop() || "overview"}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-primary text-white" : "text-muted-foreground hover:bg-accent hover:text-foreground"
              }`
            }
          >
            <n.icon size={18} strokeWidth={1.5} />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-lg px-3 py-2">
          <div className="h-9 w-9 rounded-full bg-primary/10 grid place-items-center text-sm font-bold text-primary">
            {(user?.name || "M").slice(0, 1).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user?.company || user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={doLogout} data-testid="logout-btn" className="mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground">
          <LogOut size={18} strokeWidth={1.5} /> Çıkış Yap
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-muted/20 flex">
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card fixed inset-y-0">
        <SidebarContent />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="relative w-64 flex flex-col bg-card">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:pl-64">
        <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between border-b border-border glass bg-background/70 px-4 h-14">
          <button data-testid="dash-mobile-menu" onClick={() => setOpen(true)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border">
            <Menu size={18} />
          </button>
          <Logo />
          <ThemeToggle />
        </header>
        <div className="hidden lg:flex items-center justify-end gap-2 px-8 h-14 border-b border-border bg-background/60">
          <ThemeToggle />
        </div>
        <main className="p-4 sm:p-8 max-w-7xl mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
